import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { InlineLoading } from "./Spinner";
import CodeViewer, { detectLangFromContentType, tryPrettyJson, type SyntaxLang } from "./CodeViewer";

interface Props {
  requestId: string;
  runId: string | null;
  onClose: () => void;
}

// Shows a historical request_run alongside its response. Portaled to
// <body> so the fixed-position backdrop covers the entire viewport
// (rendering it inline inside the editor pane causes the backdrop to
// start ~40px down because the main scroll container clips position:
// fixed children when an ancestor creates a containing block).
export default function RunDetailModal({ requestId, runId, onClose }: Props) {
  const q = useQuery({
    queryKey: ["run", requestId, runId],
    queryFn: () => api.requests.run_detail(requestId, runId!),
    enabled: runId != null,
  });
  useEffect(() => {
    if (runId == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [runId, onClose]);

  // Hooks must run on every render, so all the useMemo's live above
  // the runId-null short-circuit below. When runId is null the values
  // are computed against the empty/loading state and we discard the
  // modal anyway.
  const run = q.data?.run;
  // Use the response Content-Type for the response body's syntax. For
  // the request body, try the Content-Type header if the request set
  // one; otherwise fall back to plain text.
  const requestCt = run ? findHeader(run.request_headers, "content-type") : null;
  const responseCt = run?.content_type ?? null;
  const reqLang: SyntaxLang = useMemo(() => detectLangFromContentType(requestCt), [requestCt]);
  const resLang: SyntaxLang = useMemo(() => detectLangFromContentType(responseCt), [responseCt]);

  if (runId == null) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Run details</h2>
          {run && (
            <span className="text-xs muted-strong">
              {new Date(run.ran_at).toLocaleString()} · {run.duration_ms} ms
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {!q.data && <div className="col-span-2"><InlineLoading label="Loading run…" /></div>}
          {run && (
            <>
              <section className="space-y-3 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={"method-tag shrink-0 " + run.method.toLowerCase()}>{run.method}</span>
                  <code className="text-xs font-mono truncate flex-1 min-w-0" title={run.url}>{run.url}</code>
                </div>
                <Block label="Request headers">
                  <Headers headers={run.request_headers} />
                </Block>
                <BodyBlock label="Request body" body={run.request_body} lang={reqLang} />
              </section>
              <section className="space-y-3 min-w-0">
                <div className="flex items-center gap-2 text-sm flex-wrap min-w-0">
                  {run.status != null && (
                    <span className="chip bg-surface-2 muted-strong mono">{run.status}</span>
                  )}
                  {run.error && <span className="chip bg-danger/15 text-danger truncate max-w-full">{run.error}</span>}
                  {run.content_type && (
                    <span className="text-xs muted-strong mono truncate">{run.content_type}</span>
                  )}
                </div>
                <Block label="Response headers">
                  <Headers headers={run.response_headers ?? {}} />
                </Block>
                <BodyBlock label="Response body" body={run.response_body} lang={resLang} />
              </section>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="label mb-1.5">{label}</div>
      <div className="card p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-72 overflow-auto scroll-pane min-w-0">
        {children}
      </div>
    </div>
  );
}

// Body block — when we can detect a syntax (json/html/xml/css/javascript)
// we render it through the shared CodeViewer for line numbers + colors;
// otherwise we fall back to plain wrapped text. JSON gets pretty-printed
// because servers almost never return formatted JSON.
function BodyBlock({ label, body, lang }: { label: string; body: string | null | undefined; lang: SyntaxLang }) {
  if (!body) {
    return (
      <div className="min-w-0">
        <div className="label mb-1.5">{label}</div>
        <div className="card p-3 text-xs font-mono min-w-0"><span className="muted-strong italic">empty</span></div>
      </div>
    );
  }
  if (lang === "plain") {
    return (
      <div className="min-w-0">
        <div className="label mb-1.5">{label}</div>
        <div className="card p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-72 overflow-auto scroll-pane min-w-0">{body}</div>
      </div>
    );
  }
  const value = lang === "json" ? tryPrettyJson(body) : body;
  return (
    <div className="min-w-0">
      <div className="label mb-1.5 flex items-center gap-2">
        <span>{label}</span>
        <span className="text-[10px] muted-strong mono">{lang}</span>
      </div>
      <div className="h-72 min-w-0">
        <CodeViewer value={value} lang={lang} maxHeight="100%" />
      </div>
    </div>
  );
}

function Headers({ headers }: { headers: Record<string, string> }) {
  const entries = Object.entries(headers ?? {});
  if (entries.length === 0) return <span className="muted-strong italic">none</span>;
  return (
    <ul className="space-y-1">
      {entries.map(([k, v]) => (
        <li key={k} className="grid grid-cols-[minmax(120px,1fr)_minmax(0,2fr)] gap-2 min-w-0">
          <span className="text-text-faint break-all">{k}</span>
          <span className="break-all">{v}</span>
        </li>
      ))}
    </ul>
  );
}

// Headers may arrive as a plain object — find a header case-insensitively
// without losing the original casing.
function findHeader(headers: Record<string, string> | null | undefined, name: string): string | null {
  if (!headers) return null;
  const lower = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}
